import { useCallback, useMemo, useState } from "react";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import type {
    DocWithTaxonomy,
    MonthRow,
    UnverifiedDoc,
} from "./documentRetrievalHelpers";

export function useDocumentReviewDrawer({
    rawDocs,
    rows,
    unverifiedDocs,
}: {
    rawDocs: DocWithTaxonomy[];
    rows: MonthRow[];
    unverifiedDocs: UnverifiedDoc[];
}) {
    const [previewedDocumentId, setPreviewedDocumentId] = useState<
        number | null
    >(null);

    const docsById = useMemo(() => {
        const map = new Map<number, DocWithTaxonomy>();
        for (const doc of rawDocs) {
            map.set(doc.id, doc);
        }
        return map;
    }, [rawDocs]);

    const previewedDoc = previewedDocumentId
        ? (docsById.get(previewedDocumentId) ?? null)
        : null;

    const closePreview = useCallback(() => setPreviewedDocumentId(null), []);

    const openPreview = useCallback(
        (documentId: number) => setPreviewedDocumentId(documentId),
        [],
    );

    const handleRowClick = useCallback(
        (rowIdx: number) => {
            const row = rows[rowIdx];
            if (row?.documentId) {
                setPreviewedDocumentId(row.documentId);
            }
        },
        [rows],
    );

    const handleUnverifiedRowClick = useCallback(
        (rowIdx: number) => {
            const doc = unverifiedDocs[rowIdx];
            if (doc) {
                setPreviewedDocumentId(doc.id);
            }
        },
        [unverifiedDocs],
    );

    useKeyboardCommand({
        keys: "Escape",
        callback: closePreview,
        options: {
            enabled: !!previewedDoc,
            preventDefault: true,
            description: "Close document review",
        },
    });

    return {
        previewedDoc,
        closePreview,
        openPreview,
        handleRowClick,
        handleUnverifiedRowClick,
    };
}
